import React, { useState, useEffect } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import ReactTooltip from 'react-tooltip';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { paymentsShape } from 'constants/propTypes';

import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { COVID_VOUCHER_REFUND, MARGIN_REFUND } from 'constants/payment';
import BusinessAccountQuery from 'components/queries/BusinessAccountQuery';

import Button, { ConfirmButton, CancelButton } from 'components/ui/Button';
import AlertBox from 'components/ui/AlertBox';
import { CurrencyInput } from 'components/ui/Inputs';
import CardSelect from 'components/ui/CardSelect';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import AccountDetails from 'components/ui/Payments/SplitPaymentMethods/BusinessAccountPayment/AccountDetails';
import { validAccountNumber, validPostcode } from 'components/ui/Payments/SplitPaymentMethods/BusinessAccountPayment/AccountForm';

import { GET_BUSINESS_ACCOUNT_STATUS } from 'store/queries';
import { ADD_NOTE_TO_BOOKING } from 'store/mutations';
import { useMutation, useQuery } from '@apollo/client';
import { getBusinessAccountPaymentsWithAmounts } from '../../Payments.utils';
import { ErrorMessage, FormWrapper, Spacer, StyledLabel, AmountLabel, AccountCardWrapper } from './BusinessAccountRefund.style';

const getStatusTooltip = ({ loading, error, status, formatMessage }) => {
  if (loading) return null;

  if (error) {
    const errorCode = error.graphQLErrors?.[0]?.extensions?.code;
    if (errorCode === 'BUSINESS_ACCOUNT_NOT_FOUND') {
      return formatMessage({ id: 'baf.tooltip.notFound' });
    }
    return formatMessage({ id: 'baf.tooltip.technicalError' });
  }

  if (status === 'UNKNOWN') {
    return formatMessage({ id: 'baf.tooltip.technicalError' });
  }

  if (status !== 'LIVE') {
    const translatedStatus = formatMessage({ id: `baf.status.${status.toLowerCase()}` });
    return formatMessage({ id: 'baf.tooltip.statusNotLive' }, { status: translatedStatus });
  }

  return null;
};

const BusinessAccountButtonBase = ({
  intl,
  accountNumber,
  originalAmount,
  availableRefundAmount,
  currency,
  clearErrors,
  register,
  onSelect
}) => {
  const { formatMessage } = intl;
  const { loading, error, data } = useQuery(GET_BUSINESS_ACCOUNT_STATUS, {
    variables: { accountNumber }
  });

  const status = data?.businessAccountStatus;
  const isActive = status === 'LIVE';

  const hasAmounts = availableRefundAmount !== undefined;
  const availableAmount = availableRefundAmount || 0;
  const disabled = loading || error || !isActive || (hasAmounts && availableAmount <= 0);

  const statusTooltip = getStatusTooltip({ loading, error, status, formatMessage });
  const tooltipId = `baf-status-tooltip-${accountNumber}`;

  const title = !statusTooltip && hasAmounts && availableAmount <= 0 ? 'No refund amount available' : undefined;

  return (
    <AccountCardWrapper data-for={statusTooltip ? tooltipId : undefined} data-tip={statusTooltip || undefined}>
      <CardSelect
        key={accountNumber}
        id={accountNumber}
        testId={accountNumber}
        value={accountNumber}
        disabled={disabled}
        title={title}
        onClick={() => {
          clearErrors('account');
          onSelect?.();
        }}
        {...register('account', {
          required: true,
          onChange: () => clearErrors('account')
        })}
      >
        <div>{accountNumber}</div>
        {hasAmounts && (
          <div>
            <AmountLabel>
              <FormattedMessage id="refunds.original" />
            </AmountLabel>
            <FormattedCurrency
              dataCy={`original-amount-${accountNumber}`}
              dataTestId={`original-amount-${accountNumber}`}
              currency={currency}
              amount={originalAmount}
            />
          </div>
        )}
        {hasAmounts && (
          <div>
            <AmountLabel>
              <FormattedMessage id="refunds.available" />
            </AmountLabel>
            <FormattedCurrency
              dataCy={`available-amount-${accountNumber}`}
              dataTestId={`available-amount-${accountNumber}`}
              currency={currency}
              amount={availableAmount}
            />
          </div>
        )}
      </CardSelect>
      {statusTooltip && <ReactTooltip id={tooltipId} />}
    </AccountCardWrapper>
  );
};

BusinessAccountButtonBase.propTypes = {
  intl: PropTypes.shape({ formatMessage: PropTypes.func }).isRequired,
  accountNumber: PropTypes.string.isRequired,
  originalAmount: PropTypes.number,
  availableRefundAmount: PropTypes.number,
  currency: PropTypes.string,
  clearErrors: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  onSelect: PropTypes.func
};

export const BusinessAccountButton = injectIntl(BusinessAccountButtonBase);

const BusinessAccountRefund = ({
  onRefund,
  addPayment,
  addPayoutAndBalance,
  bookingReference,
  currency = 'GBP',
  loading,
  method,
  reference,
  totalPriceToBePaid = 0,
  updatePayments,
  isCancelVoucher,
  mainBookingRef,
  isOverpaid = false,
  payments = []
}) => {
  const [addNote] = useMutation(ADD_NOTE_TO_BOOKING);
  const [isAccountNotFound, setAccountNotFound] = useState(false);

  const allBAFPayments = getBusinessAccountPaymentsWithAmounts(payments);

  const [maxAccountAmount, setMaxAccountAmount] = useState(totalPriceToBePaid);

  const { register, handleSubmit, clearErrors, formState, watch, trigger } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: totalPriceToBePaid
    }
  });

  const { errors } = formState;

  const {
    register: registerSearchField,
    getValues: getAccountNumberAndPostcode,
    formState: businessAccountFormState
  } = useForm({ mode: 'onChange', reValidateMode: 'onChange' });

  const { errors: businessAccountErrors } = businessAccountFormState;

  const paymentAmount = watch('amount');

  useEffect(() => {
    trigger('amount');
  }, [maxAccountAmount, trigger]);

  const onSubmit = ({ account, amount }) => {
    const completeTransaction = amount >= totalPriceToBePaid - MARGIN_REFUND;

    onRefund({
      addPayment,
      addPayoutAndBalance,
      bookingReference,
      completeTransaction,
      currency,
      externalReference: account,
      method,
      paymentAmount: amount,
      reference,
      totalPriceToBePaid,
      updatePayments,
      isOverpaid
    });

    if (isCancelVoucher) {
      addNote({
        variables: {
          note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
          bookingReference: mainBookingRef
        }
      });

      addNote({
        variables: {
          note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
          bookingReference
        }
      });
    }
  };

  const hasAccountError = Boolean(errors.account);
  const hasAmountError = Boolean(errors.amount);
  const hasAccountNumberError = Boolean(businessAccountErrors.accountNumber);
  const hasPostcodeError = Boolean(businessAccountErrors.postcode);

  if (isAccountNotFound) {
    return (
      <>
        <AlertBox variant={AlertBox.variants.ERROR}>
          <FormattedMessage id="baf.accountNotFound" />
        </AlertBox>
        <CancelButton onClick={() => setAccountNotFound(false)}>
          <FormattedMessage id="baf.new" />
        </CancelButton>
      </>
    );
  }

  return (
    <BusinessAccountQuery
      onAccountNotFound={() => setAccountNotFound(true)}
      onError={(err) => {
        throw new AgentPortalError(err, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
          errorCTARedirect: '/'
        });
      }}
    >
      {(searchForBusinessAccount, { loading: businessAccountLoading, data: businessAccountData, resetQuery }) => (
        <>
          <header>
            <h2 data-cy="title">
              <FormattedMessage id="refunds.businessAccount.title" />
            </h2>
            <p data-cy="paragraph">
              <FormattedMessage id="refunds.businessAccount.paragraph" />
            </p>
          </header>

          <form data-testid="refund-form" data-cy="refund-form" onSubmit={handleSubmit(onSubmit)}>
            <FormWrapper>
              <Spacer>
                <CurrencyInput
                  currency={currency}
                  dataCy="amount-input"
                  dataTestId="amount-input"
                  defaultValue={totalPriceToBePaid}
                  isControlled={false}
                  {...register('amount', {
                    required: true,
                    min: 0.01,
                    max: maxAccountAmount
                  })}
                  step={0.01}
                />
              </Spacer>
              {allBAFPayments.map(({ accountNumber, originalAmount, availableRefundAmount }) => (
                <BusinessAccountButton
                  key={accountNumber}
                  accountNumber={accountNumber}
                  originalAmount={originalAmount}
                  availableRefundAmount={availableRefundAmount}
                  currency={currency}
                  clearErrors={clearErrors}
                  register={register}
                  onSelect={() => setMaxAccountAmount(availableRefundAmount)}
                />
              ))}
              {hasAmountError && (
                <>
                  <ErrorMessage data-cy="amount-error-msg">
                    <FormattedMessage
                      id="refunds.businessAccount.error.amount"
                      values={{
                        amount: <FormattedCurrency currency={currency} amount={maxAccountAmount} />
                      }}
                    />
                  </ErrorMessage>
                  <ErrorMessage data-cy="baf-max-amount-error-msg">
                    <FormattedMessage
                      id="refunds.businessAccount.error.maxAmount"
                      values={{
                        amount: <FormattedCurrency currency={currency} amount={maxAccountAmount} />
                      }}
                    />
                  </ErrorMessage>
                </>
              )}
              {hasAccountError && (
                <ErrorMessage data-cy="account-error-msg">
                  <FormattedMessage id="refunds.businessAccount.error.account" />
                </ErrorMessage>
              )}
              {allBAFPayments.length > 0 && (
                <>
                  <Button
                    data-testid="submit"
                    data-cy="submit"
                    type="submit"
                    loading={loading}
                    disabled={loading || hasAmountError}
                    variant="primary"
                  >
                    <FormattedMessage id="refunds.businessAccount.submit" />
                  </Button>
                  <p>
                    <FormattedMessage id="baf.refundToNew" />
                  </p>
                </>
              )}

              {businessAccountData ? (
                <>
                  <AccountDetails currency={currency} account={businessAccountData} resetQuery={resetQuery} />
                  <div>
                    <ConfirmButton
                      loading={loading}
                      disabled={loading}
                      onClick={(e) => {
                        e.stopPropagation();

                        onSubmit({
                          account: businessAccountData.accountNumber,
                          amount: paymentAmount
                        });
                        resetQuery();
                      }}
                    >
                      <FormattedMessage id="refunds.businessAccount.submit" />
                    </ConfirmButton>
                  </div>
                </>
              ) : (
                <div>
                  <div>
                    <label htmlFor="checkInDate">
                      <StyledLabel>
                        <FormattedMessage id="baf.bafCode" />
                      </StyledLabel>
                      <input {...registerSearchField('accountNumber', { validate: validAccountNumber })} />
                    </label>
                  </div>

                  {hasAccountNumberError && (
                    <ErrorMessage>
                      <FormattedMessage id="baf.invalidAccountNumber" />
                    </ErrorMessage>
                  )}

                  <div>
                    <label>
                      <StyledLabel>
                        <FormattedMessage id="baf.enterPostcode" />
                      </StyledLabel>
                      <input {...registerSearchField('postcode', { validate: validPostcode })} />
                      {hasPostcodeError && (
                        <ErrorMessage>
                          <FormattedMessage id="baf.invalidPostcode" />
                        </ErrorMessage>
                      )}
                    </label>
                  </div>
                  <Button
                    loading={businessAccountLoading}
                    disabled={businessAccountLoading || !formState.isValid}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();

                      const values = getAccountNumberAndPostcode();

                      searchForBusinessAccount(values);
                    }}
                  >
                    <FormattedMessage id="baf.check" />
                  </Button>
                </div>
              )}
            </FormWrapper>
          </form>
        </>
      )}
    </BusinessAccountQuery>
  );
};

BusinessAccountRefund.propTypes = {
  addPayment: PropTypes.func,
  addPayoutAndBalance: PropTypes.func,
  bookingReference: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  method: PropTypes.string.isRequired,
  onRefund: PropTypes.func.isRequired,
  reference: PropTypes.string.isRequired,
  totalPriceToBePaid: PropTypes.number.isRequired,
  updatePayments: PropTypes.func,
  isCancelVoucher: PropTypes.bool,
  mainBookingRef: PropTypes.string,
  isOverpaid: PropTypes.bool,
  payments: paymentsShape
};

export default BusinessAccountRefund;
